import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Plus, X } from 'lucide-react';
import { useSupplierMutations } from '@/features/suppliers/hooks/useSupplierMutations';
import { usePageTitle } from '@/contexts/PageTitleContext';
import {
  serializeAddress,
  serializeEmails,
  COUNTRY_OPTIONS,
} from '@/features/suppliers/types';
import type { StructuredAddress } from '@/features/suppliers/types';

const DEFAULT_ADDRESS: StructuredAddress = {
  street: '',
  suburb: '',
  state: '',
  postcode: '',
  country: 'Australia',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CURRENCY_OPTIONS = ['AUD', 'USD', 'CNY', 'EUR', 'GBP'] as const;

const parseProductsInput = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

export default function SupplierNew() {
  const navigate = useNavigate();
  const { createSupplier } = useSupplierMutations();

  const [formData, setFormData] = useState({
    title: '',
    contact_name: '',
    phone: '',
    default_currency: '',
    payment_terms_days: '',
    deposit_percent: '',
    minimum_order_quantity: '',
    products: '',
    description: '',
    lead_time_standard_days: '',
    lead_time_sample_days: '',
    lead_time_urgent_days: '',
  });

  const [address, setAddress] = useState<StructuredAddress>({ ...DEFAULT_ADDRESS });
  const [hidePricesOnPdf, setHidePricesOnPdf] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;

    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (emails.includes(trimmed)) {
      setEmailError('This email has already been added');
      return;
    }

    setEmails((prev) => [...prev, trimmed]);
    setEmailInput('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const paymentTermsDaysParsed = formData.payment_terms_days.trim()
      ? Number.parseInt(formData.payment_terms_days.trim(), 10)
      : Number.NaN;
    const depositPercentParsed = formData.deposit_percent.trim()
      ? Number.parseInt(formData.deposit_percent.trim(), 10)
      : Number.NaN;
    const parseLeadTime = (raw: string): number | null => {
      const trimmed = raw.trim();
      if (!trimmed) return null;
      const parsed = Number.parseInt(trimmed, 10);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    };
    const leadTimeStandard = parseLeadTime(formData.lead_time_standard_days);
    const leadTimeSample = parseLeadTime(formData.lead_time_sample_days);
    const leadTimeUrgent = parseLeadTime(formData.lead_time_urgent_days);

    // Auto-include any pending email typed in the input field
    let finalEmails = emails;
    const pendingEmail = emailInput.trim();
    if (pendingEmail && EMAIL_REGEX.test(pendingEmail) && !emails.includes(pendingEmail)) {
      finalEmails = [...emails, pendingEmail];
    }

    const payload = {
      title: formData.title.trim(),
      contact_name: formData.contact_name.trim(),
      email: serializeEmails(finalEmails),
      phone: formData.phone.trim(),
      address: serializeAddress(address),
      minimum_order_quantity: formData.minimum_order_quantity.trim(),
      products: parseProductsInput(formData.products),
      description: formData.description.trim(),
      default_currency: formData.default_currency.trim(),
      payment_terms_days: Number.isFinite(paymentTermsDaysParsed) ? paymentTermsDaysParsed : null,
      deposit_percent: Number.isFinite(depositPercentParsed) ? depositPercentParsed : null,
      lead_time_standard_days: leadTimeStandard,
      lead_time_sample_days: leadTimeSample,
      lead_time_urgent_days: leadTimeUrgent,
      hide_prices_on_pdf: hidePricesOnPdf,
    };

    createSupplier.mutate(payload, {
      onSuccess: () => {
        navigate('/suppliers');
      },
    });
  };

  const isLoading = createSupplier.isPending;

  // "Back to Suppliers" lives in the TopHeader (typewriter button).
  usePageTitle('New Supplier', 'Add a new supplier to your centralized database.', {
    label: 'Back to Suppliers',
    to: '/suppliers',
  });

  return (
    <div className="flex flex-col">
      <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit}>
          <div className="max-w-3xl space-y-6">
            {/* Basic Details */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Supplier Name *</Label>
                  <Input
                    id="title"
                    type="text"
                    placeholder="e.g., Vast Pins"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_name">Main Contact Name</Label>
                  <Input
                    id="contact_name"
                    type="text"
                    placeholder="e.g., Echo"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    disabled={isLoading}
                  />
                </div>

                {/* Email (multi) */}
                <div className="space-y-2">
                  <Label>Email Addresses</Label>
                  {emails.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {emails.map((email) => (
                        <Badge key={email} variant="secondary" className="gap-1 pr-1 text-xs">
                          {email}
                          <button
                            type="button"
                            onClick={() => handleRemoveEmail(email)}
                            className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                            disabled={isLoading}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="name@example.com"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailError('');
                      }}
                      onKeyDown={handleEmailInputKeyDown}
                      disabled={isLoading}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddEmail}
                      disabled={isLoading || !emailInput.trim()}
                      className="shrink-0"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  {emailError && (
                    <p className="text-xs text-destructive">{emailError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Contact Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Optional"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card>
              <CardHeader>
                <CardTitle>Address</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="addr_street" className="text-sm">Street Address</Label>
                  <Input
                    id="addr_street"
                    type="text"
                    placeholder="123 Main Street"
                    value={address.street}
                    onChange={(e) => setAddress({ ...address, street: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="addr_suburb" className="text-sm">Suburb</Label>
                    <Input
                      id="addr_suburb"
                      type="text"
                      placeholder="Suburb"
                      value={address.suburb}
                      onChange={(e) => setAddress({ ...address, suburb: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="addr_state" className="text-sm">State</Label>
                    <Input
                      id="addr_state"
                      type="text"
                      placeholder="State"
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="addr_postcode" className="text-sm">Postcode</Label>
                    <Input
                      id="addr_postcode"
                      type="text"
                      placeholder="Postcode"
                      value={address.postcode}
                      onChange={(e) => setAddress({ ...address, postcode: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="addr_country" className="text-sm">Country</Label>
                    <Select
                      value={address.country}
                      onValueChange={(value) => setAddress({ ...address, country: value })}
                      disabled={isLoading}
                    >
                      <SelectTrigger id="addr_country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_OPTIONS.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Products & Terms */}
            <Card>
              <CardHeader>
                <CardTitle>Products & Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="minimum_order_quantity">Minimum Order Quantity</Label>
                    <Input
                      id="minimum_order_quantity"
                      type="text"
                      placeholder="e.g., 500 or No MOQ"
                      value={formData.minimum_order_quantity}
                      onChange={(e) =>
                        setFormData({ ...formData, minimum_order_quantity: e.target.value })
                      }
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="products">Products Offered</Label>
                    <Input
                      id="products"
                      type="text"
                      placeholder="Comma-separated list"
                      value={formData.products}
                      onChange={(e) => setFormData({ ...formData, products: e.target.value })}
                      disabled={isLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Example: Hats, Socks, Stickers
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="default_currency">Default Currency</Label>
                    <Select
                      value={formData.default_currency || '__org_default__'}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          default_currency: value === '__org_default__' ? '' : value,
                        })
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger id="default_currency">
                        <SelectValue placeholder="Org default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__org_default__">Org default</SelectItem>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <SelectItem key={currency} value={currency}>
                            {currency}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="payment_terms_days">Payment Terms (days)</Label>
                    <Input
                      id="payment_terms_days"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      placeholder="e.g., 30"
                      value={formData.payment_terms_days}
                      onChange={(e) => setFormData({ ...formData, payment_terms_days: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="deposit_percent">Deposit (%)</Label>
                    <Input
                      id="deposit_percent"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      placeholder="e.g., 0"
                      value={formData.deposit_percent}
                      onChange={(e) => setFormData({ ...formData, deposit_percent: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Phase A — lead time fields */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="lead_time_standard_days">Standard lead time (days)</Label>
                    <Input
                      id="lead_time_standard_days"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      placeholder="Optional"
                      value={formData.lead_time_standard_days}
                      onChange={(e) =>
                        setFormData({ ...formData, lead_time_standard_days: e.target.value })
                      }
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lead_time_sample_days">Sample lead time (days)</Label>
                    <Input
                      id="lead_time_sample_days"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      placeholder="Optional"
                      value={formData.lead_time_sample_days}
                      onChange={(e) =>
                        setFormData({ ...formData, lead_time_sample_days: e.target.value })
                      }
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lead_time_urgent_days">Urgent lead time (days)</Label>
                    <Input
                      id="lead_time_urgent_days"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      placeholder="Optional"
                      value={formData.lead_time_urgent_days}
                      onChange={(e) =>
                        setFormData({ ...formData, lead_time_urgent_days: e.target.value })
                      }
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description of Goods Offered</Label>
                  <Textarea
                    id="description"
                    placeholder="Optional"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={isLoading}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="hide_prices">Hide prices on PO PDFs</Label>
                    <p className="text-xs text-muted-foreground">
                      When enabled, unit costs and totals are excluded from purchase order PDFs sent to this supplier.
                    </p>
                  </div>
                  <Switch
                    id="hide_prices"
                    checked={hidePricesOnPdf}
                    onCheckedChange={setHidePricesOnPdf}
                    disabled={isLoading}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/suppliers')}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Supplier'
                )}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
